import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    google?: any;
  }
}

/**
 * Google Workspace sign-in — AUTH ONLY.
 *
 * Replaces the deleted `google-auth.service.ts`, which was a prototype that minted forged tokens,
 * kept credentials in localStorage, and read the caller's TENANT out of an unvalidated JWT payload.
 * None of that shape survives here.
 *
 * ── WHY THIS IS NOT THE PKCE CODE FLOW ────────────────────────────────────────────────────────
 * The design called for "PKCE" across the board. That is right for the DESKTOP, which runs an
 * authorization-CODE flow through a loopback listener and exchanges the code server-side
 * (DataServices /api/auth/google/exchange, so no client secret ships to a field unit).
 *
 * It is the wrong tool here. PKCE protects an authorization CODE in transit, and this flow never
 * produces one: Google Identity Services hands the browser an ID TOKEN directly, which is the only
 * artifact DataServices needs. Adding a code flow to the SPA would mean a code exchange, which means
 * a client secret somewhere, which is the thing we are avoiding. So the SPA asks for the token it
 * actually needs and nothing more.
 *
 * What replaces PKCE's job here is the NONCE: generated per attempt with crypto.getRandomValues,
 * sent on the request, and echoed inside the signed token. A replayed token carries the wrong nonce.
 *
 * ── SCOPES ARE FROZEN ─────────────────────────────────────────────────────────────────────────
 * `openid email profile`, and nothing else, ever. That freeze is the entire reason this integration
 * needs no Google verification review, shows no "unverified app" screen, needs no CASA assessment,
 * and has no 100-user cap. Adding one Drive/Gmail/Directory scope re-arms all four. This flow
 * requests no scopes at all (an ID token is implicit in `accounts.id`), which is the strongest form
 * of that guarantee: there is no scope string here to widen.
 *
 * ── THE TOKEN LIVES IN MEMORY ─────────────────────────────────────────────────────────────────
 * Never localStorage — that was XSS-readable and survived tab close. The cost is that a page reload
 * ends the Google session and the user signs in again. That is the accepted consequence of the
 * direct-ID-token model: a browser public client cannot silently renew a Google ID token (no
 * refresh token, and hidden-iframe renewal is dead with third-party cookie restrictions), so the
 * session is ~1 hour and a reload is simply the same re-auth arriving sooner.
 */
@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
  private static readonly GSI_SRC = 'https://accounts.google.com/gsi/client';

  /** In memory only, by design. */
  private idToken: string | null = null;
  private expiresAtMs = 0;
  private nonce: string | null = null;
  private scriptLoading: Promise<void> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly _signedIn = new BehaviorSubject<boolean>(false);
  readonly signedIn$: Observable<boolean> = this._signedIn.asObservable();

  private readonly _email = new BehaviorSubject<string | null>(null);
  readonly email$: Observable<string | null> = this._email.asObservable();

  /**
   * SELF-GATING. No client id configured ⇒ this provider does not exist as far as the UI is
   * concerned, and no script is loaded and no call is made to Google.
   *
   * This is what lets the rebuilt sign-in ship BEFORE the OAuth clients are created: with the
   * setting empty the build is byte-identical in behaviour to one without Google at all, and
   * turning it on later is a config change rather than another code change and review cycle.
   *
   * It is also the lesson from the retired client: the previous service initialised Google
   * Identity Services from its CONSTRUCTOR, so hiding the button did not stop it contacting Google
   * against a client we did not own. Nothing here runs until someone asks it to.
   */
  get isConfigured(): boolean {
    return !!(environment as any).googleClientId;
  }

  private get clientId(): string {
    return (environment as any).googleClientId || '';
  }

  /**
   * The bearer to send, or null when there is no usable Google session.
   *
   * PURE. It reads and returns; it does not mutate.
   *
   * It used to call clear() when it found an expired token, which made a get-named method mutate
   * state — and because the interceptor calls it, that mutation sat on the hot path of EVERY API
   * request, where it could emit on signedIn$ in the middle of an interception. Expiry is now
   * handled by a timer armed when the token arrives (see scheduleExpiry), so the state transition
   * happens once, at a predictable moment, off the request path.
   *
   * Treated as expired 60s early so a token cannot lapse mid-request — the same margin the desktop
   * applies in UserToken.IsValid.
   */
  getIdToken(): string | null {
    if (!this.idToken) return null;
    if (Date.now() > this.expiresAtMs - 60_000) return null;
    return this.idToken;
  }

  /** Also pure — it is getIdToken's answer, nothing more. */
  isSignedIn(): boolean {
    return this.getIdToken() !== null;
  }

  /**
   * Clears the session the moment the token stops being usable, so the UI stops claiming a sign-in
   * that no longer works. Armed on receipt rather than checked on read, which is what keeps
   * getIdToken pure.
   */
  private scheduleExpiry(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }

    // The same 60s margin getIdToken applies, so the two cannot disagree about when a token died.
    const delay = this.expiresAtMs - 60_000 - Date.now();

    if (delay <= 0) {
      this.clear();
      return;
    }

    this.expiryTimer = setTimeout(() => this.clear(), delay);
  }

  /**
   * Renders Google's own button into `host`. Google requires its rendered button for branding, and
   * it also means the click-to-credential path is theirs rather than ours.
   */
  async renderButton(host: HTMLElement, loginHint?: string): Promise<void> {
    if (!this.isConfigured) return;
    await this.loadScript();

    this.nonce = this.newNonce();

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (resp: any) => this.onCredential(resp),
      nonce: this.nonce,
      auto_select: false,
      // The email typed on /login (home-realm discovery routed it here), so Google preselects it.
      ...(loginHint ? { login_hint: loginHint } : {}),
      cancel_on_tap_outside: true,
      // Workspace accounts only. Google puts `hd` in the ID token ONLY when the request asked for
      // it ("only present if ... the client specified the hd parameter", Google Cloud "Token types",
      // 2026-09-30), and DataServices REFUSES a Google token without hd. '*' = any Workspace
      // domain, and keeps consumer accounts out of the picker. hd carries the account's PRIMARY
      // Workspace domain, which is why a BYOT-Google customer must list that domain.
      hd: '*',
      // No One Tap. It can sign a user in without an explicit click, which is the wrong default for
      // an app where the account chosen decides which company's data is shown.
    });

    window.google.accounts.id.renderButton(host, {
      theme: 'outline',
      size: 'large',
      type: 'standard',
      shape: 'rectangular',
      text: 'signin_with',
      logo_alignment: 'left',
    });
  }

  /**
   * Google hands us the signed ID token. We do NOT validate it here and must not pretend to:
   * signature, issuer, audience, hd and email_verified are all checked SERVER-side by
   * DataServices. The only local read is `exp`, and only to know when to stop sending it.
   *
   * The deleted prototype read the TENANT out of this payload and trusted it. Nothing here derives
   * authorization from the token; the server resolves the company from the verified `sub` and `hd`.
   */
  private onCredential(resp: any): void {
    const credential = resp?.credential;
    if (!credential) return;

    const payload = this.readExpAndNonce(credential);
    if (!payload) return;

    // A token whose nonce does not match the one we just generated is not from this attempt.
    if (this.nonce && payload.nonce !== this.nonce) {
      this.clear();
      return;
    }

    this.idToken = credential;
    this.expiresAtMs = payload.expMs;
    this.scheduleExpiry();
    this._email.next(payload.email);
    this._signedIn.next(true);
  }

  /**
   * Reads ONLY `exp`, `nonce` and `email` from the payload, for expiry, replay and display.
   * Deliberately not a general decoder: the previous one returned a `tenantId` and that field is
   * how an unvalidated claim became an authorization input.
   */
  private readExpAndNonce(
    jwt: string
  ): { expMs: number; nonce?: string; email: string | null } | null {
    try {
      const part = jwt.split('.')[1];
      if (!part) return null;
      const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
      const p = JSON.parse(json);
      if (typeof p.exp !== 'number') return null;
      return {
        expMs: p.exp * 1000,
        nonce: p.nonce,
        email: typeof p.email === 'string' ? p.email : null,
      };
    } catch {
      return null;
    }
  }

  signOut(): void {
    try {
      window.google?.accounts?.id?.disableAutoSelect();
    } catch {
      /* the session is ending either way */
    }
    this.clear();
  }

  private clear(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
    this.idToken = null;
    this.expiresAtMs = 0;
    this.nonce = null;
    this._email.next(null);
    this._signedIn.next(false);
  }

  /** crypto.getRandomValues, not Math.random — the prototype used the latter for its nonce. */
  private newNonce(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private loadScript(): Promise<void> {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (this.scriptLoading) return this.scriptLoading;

    this.scriptLoading = new Promise<void>((resolve, reject) => {
      const el = document.createElement('script');
      el.src = GoogleIdentityService.GSI_SRC;
      el.async = true;
      el.defer = true;
      el.onload = () => resolve();
      el.onerror = () => reject(new Error('Google Identity Services failed to load'));
      document.head.appendChild(el);
    });
    return this.scriptLoading;
  }
}
